import { isNativePlatform } from '../runtime'
import { nativeAdapterUnavailable } from './unavailable'
import { NativeSecureStorage } from './nativeSecureStorage'

/**
 * The NATIVE_DEVICE id (#1691, spec.md §5.3): a UUID minted once on first
 * launch and persisted for the life of this install, so `registerNativeDevice`
 * upserts the SAME Firestore doc every call instead of creating a new one —
 * that reuse is what makes registration idempotent (see
 * `functions/nativeApp/registerNativeDevice.js`'s header).
 *
 * Stored through the same Keychain/Keystore-backed plugin as the session
 * token (`nativeSecureStorage.js`) rather than a plain native preference
 * store: the id is not itself a secret, but keeping it in the one place this
 * app already has for durable per-install identity avoids a second native
 * storage mechanism for a single string. `clearDeviceId()` is called after a
 * successful `revokeNativeDevice` (self-revoke = logout): per
 * `nativeDeviceModel.js`'s terminal-status decision, a revoked device id is
 * never reused, so the next login on this same physical install mints a
 * fresh one and registers a new NATIVE_DEVICE doc.
 *
 * Native-only by construction — there is no NATIVE_DEVICE concept on web, so
 * unlike `authStorage.js` (which has a real, unchanged web behaviour to fall
 * back to) this throws on web too rather than returning null: any web caller
 * reaching this is a bug in the caller, not a state to degrade into.
 */
const DEVICE_ID_KEY = 'krav_native_device_id'

export async function getOrCreateDeviceId() {
  if (!isNativePlatform()) {
    return nativeAdapterUnavailable('deviceIdentity.getOrCreateDeviceId')
  }
  const { value } = await NativeSecureStorage.get({ key: DEVICE_ID_KEY })
  if (value) {
    return value
  }
  const deviceId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  await NativeSecureStorage.set({ key: DEVICE_ID_KEY, value: deviceId })
  return deviceId
}

export async function clearDeviceId() {
  if (!isNativePlatform()) {
    return nativeAdapterUnavailable('deviceIdentity.clearDeviceId')
  }
  await NativeSecureStorage.remove({ key: DEVICE_ID_KEY })
}
